// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { hapticLight } from '../../native/haptics.js';
import {
  AccountsIcon,
  RegisterIcon,
  AddIcon,
  ReportsIcon,
  MoreIcon,
} from './icons.js';

// DECISION (Phase 3): the desktop's persistent sidebar is replaced on the native
// shell by a 5-slot bottom tab bar — the primary reach-with-your-thumb navigation
// on phones. The center "Add" is deliberately the odd one out: larger, accent-
// filled, elevated. Quick capture of a purchase before it's forgotten is the
// entire reason the mobile app exists, so it gets the most prominent target.
type TabKey = 'accounts' | 'register' | 'add' | 'reports' | 'more';

function activeTab(pathname: string): TabKey {
  if (pathname === '/accounts' || pathname === '/') return 'accounts';
  if (pathname.startsWith('/accounts/')) return 'register';
  if (pathname.startsWith('/reports') || pathname.startsWith('/portfolio')) return 'reports';
  if (
    pathname.startsWith('/more') ||
    pathname.startsWith('/settings') ||
    pathname.startsWith('/search') ||
    pathname.startsWith('/budgets') ||
    pathname.startsWith('/scheduled') ||
    pathname.startsWith('/import')
  ) {
    return 'more';
  }
  return 'accounts';
}

export function BottomTabBar() {
  const { t } = useTranslation(['common', 'mobile', 'errors']);
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useApp();
  const current = activeTab(location.pathname);

  function go(to: string) {
    void hapticLight();
    navigate(to);
  }

  // The Register tab opens the most-recently-viewed account, falling back to the
  // account list when nothing has been opened yet.
  function openRegister() {
    const recent = state.recentAccountIds[0];
    go(recent ? `/accounts/${recent}` : '/accounts');
  }

  const color = (key: TabKey) => (current === key ? 'text-accent' : 'text-text-muted');

  return (
    <nav className="shrink-0 bg-bg-surface border-t border-border tabbar-inset hide-on-keyboard no-print">
      <div className="flex items-stretch justify-around h-14">
        <TabButton label={t('nav.accounts')} className={color('accounts')} onClick={() => go('/accounts')}>
          <AccountsIcon />
        </TabButton>
        <TabButton label={t('nav.register')} className={color('register')} onClick={openRegister}>
          <RegisterIcon />
        </TabButton>

        {/* Center Add — elevated accent action. */}
        <button
          onClick={() => go('/quick-entry')}
          aria-label={t('a11y.addTransaction')}
          className="relative -mt-4 flex flex-col items-center justify-center"
        >
          <span className="flex items-center justify-center w-14 h-14 bg-accent text-white border-4 border-bg-surface shadow-lg active:bg-accent-hover">
            <AddIcon size={26} strokeWidth={2} />
          </span>
        </button>

        <TabButton label={t('nav.reports')} className={color('reports')} onClick={() => go('/reports')}>
          <ReportsIcon />
        </TabButton>
        <TabButton label={t('nav.more')} className={color('more')} onClick={() => go('/more')}>
          <MoreIcon />
        </TabButton>
      </div>
    </nav>
  );
}

function TabButton({
  label,
  className,
  onClick,
  children,
}: {
  label: string;
  className: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center gap-0.5 ${className} active:opacity-70`}
    >
      {children}
      <span className="text-[10px] tracking-wide">{label}</span>
    </button>
  );
}
